import { useState, useEffect, useRef, useCallback } from 'react';
import { Modal, Spinner, Table, Fade } from 'react-bootstrap';

import AddButton from '../components/AddButton';
import ADSearchField from '../components/ADSearchField';
import ButtonType from '../components/ButtonType';
import Delayed from '../components/Delayed';
import LoadingWrapper from '../components/LoadingWrapper';
import ProjectDropdown from '../components/ProjectDropdown';
import { infoToast } from '../components/Toasty';

import { RoleGroup } from '../enums/RoleGroup';

import DashboardService from '../services/DashboardService';
import EmployeeService from '../services/EmployeeService';
import RoleGroupService from '../services/RoleGroupService';

import IProjectList from '../types/Dashboard/ProjectList';
import EmployeeInfo from '../types/Employee/EmployeeInfo';
import RoleGroupList from '../types/RoleGroup/RoleGroupList';
import RoleGroupMember from '../types/RoleGroup/RoleGroupMember';

type Props = {
    currentUser: EmployeeInfo,
    canEdit: boolean,
    isMocked: boolean,
    cookieName?: string,
}

// Constant variables for Save Button Visuals
const SAVEERROR = "SaveError";
const SAVESUCCESS = "SaveSuccess";

const initialGroupMemberUpdates: RoleGroupList = {
    addUpdates: [],
    removeUpdates: [],
    updatedGroup: [],
    companyID: "",
    groupName: 1,
    modifiedUser: ""
};

const initialAddedEmployee: RoleGroupMember = {
    employeeID: "",
    employeeName: ""
};

export default function DOTCoordinatorsPage({ currentUser, canEdit, isMocked, cookieName }: Props) {
    const [projectLoading, setProjectLoading] = useState(true);
    const [selectedProject, setSelectedProject] = useState("");
    const [projectList, setProjectList] = useState<IProjectList>({ projects: [], defaultProjectID: "" });
    const [roleGroup] = useState<RoleGroup>(1);
    const [groupLoading, setGroupLoading] = useState(false);
    const [groupMemberUpdates, setGroupMemberUpdates] = useState<RoleGroupList>(initialGroupMemberUpdates);
    const [changesMade, setChangesMade] = useState(false);
    const [addedEmployee, setAddedEmployee] = useState<RoleGroupMember>(initialAddedEmployee);
    const [userAlreadyAdded, setUserAlreadyAdded] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const [modalType, setModalType] = useState("");
    const [saveLoading, setSaveLoading] = useState(false);
    const [saveStatus, setSaveStatus] = useState("");
    const [showSaveStatus, setShowSaveStatus] = useState(false);

    const saveStatusTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const getGroupMembers = useCallback(async (project: string, group: RoleGroup) => {
        const userEmail = currentUser.email;
        if (!userEmail || !project) return;

        await RoleGroupService.getGroupMembers(project, group, userEmail)
            .then((response) => {
                if (response.data) {
                    setGroupMemberUpdates(prev => ({
                        ...prev,
                        updatedGroup: response.data.roleGroupMembers
                    }));
                    setGroupLoading(false);
                }
            });
    }, [currentUser.email]);

    // Initial data load
    useEffect(() => {
        if (!currentUser.email) return;

        const getUserProjects = async () => {
            await DashboardService.getUserProjects(currentUser.email)
                .then((response) => {
                    setProjectLoading(false);
                    setProjectList(response.data);
                    if (response.data.defaultProjectID) {
                        setSelectedProject(response.data.defaultProjectID);
                    }
                }).catch(() => {
                    setProjectLoading(false);
                });
        };

        getUserProjects();
    }, [currentUser.email]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (saveStatusTimeoutRef.current) {
                clearTimeout(saveStatusTimeoutRef.current);
            }
        };
    }, []);

    const addMemberLocal = useCallback((member: RoleGroupMember) => {
        setGroupMemberUpdates(prev => {
            const isInRemoveUpdates = prev.removeUpdates.some(x => x.employeeID === member.employeeID);

            let newAddUpdates: RoleGroupMember[];
            let newRemoveUpdates: RoleGroupMember[];

            if (isInRemoveUpdates) {
                // Remove from removeUpdates
                newRemoveUpdates = prev.removeUpdates.filter(x => x.employeeID !== member.employeeID);
                newAddUpdates = prev.addUpdates;
            } else {
                // Add to addUpdates
                newAddUpdates = [...prev.addUpdates, member];
                newRemoveUpdates = prev.removeUpdates;
            }

            const newUpdatedGroup = [...prev.updatedGroup, member];

            // Check for updates
            const hasChanges = newAddUpdates.length > 0 || newRemoveUpdates.length > 0;
            setChangesMade(hasChanges);

            return {
                ...prev,
                addUpdates: newAddUpdates,
                removeUpdates: newRemoveUpdates,
                updatedGroup: newUpdatedGroup
            };
        });

        setOpenModal(false);
        setAddedEmployee(initialAddedEmployee);
    }, []);

    const removeMemberLocal = useCallback((member: RoleGroupMember) => {
        setGroupMemberUpdates(prev => {
            const isInAddUpdates = prev.addUpdates.some(x => x.employeeID === member.employeeID);

            let newAddUpdates: RoleGroupMember[];
            let newRemoveUpdates: RoleGroupMember[];

            if (isInAddUpdates) {
                // Remove from addUpdates
                newAddUpdates = prev.addUpdates.filter(x => x.employeeID !== member.employeeID);
                newRemoveUpdates = prev.removeUpdates;
            } else {
                // Add to removeUpdates
                newRemoveUpdates = [...prev.removeUpdates, member];
                newAddUpdates = prev.addUpdates;
            }

            // Remove from updatedGroup
            const newUpdatedGroup = prev.updatedGroup.filter(x => x.employeeID !== member.employeeID);

            // Check for updates
            const hasChanges = newAddUpdates.length > 0 || newRemoveUpdates.length > 0;
            setChangesMade(hasChanges);

            return {
                ...prev,
                addUpdates: newAddUpdates,
                removeUpdates: newRemoveUpdates,
                updatedGroup: newUpdatedGroup
            };
        });
    }, []);

    const onCloseModal = useCallback(() => {
        setOpenModal(false);
        setModalType("");
        setAddedEmployee(initialAddedEmployee);
        setUserAlreadyAdded(false);
    }, []);

    const saveUpdates = useCallback(async () => {
        // if we are logged in as a different user, do not allow the confirm actions to happen
        if (isMocked) {
            infoToast('You have clicked on the "Confirm" button.  This has been disabled because you are currently not yourself.');
            setOpenModal(false);
            return;
        }

        setSaveLoading(true);
        setChangesMade(false);
        setShowSaveStatus(true);

        // Close modal
        onCloseModal();

        // Add and remove members if there are any in the lists
        if (groupMemberUpdates.addUpdates.length > 0 || groupMemberUpdates.removeUpdates.length > 0) {
            await RoleGroupService.updateGroupMembers(groupMemberUpdates)
                .then((response) => {
                    if (response.status >= 100 && response.status <= 399) {
                        setSaveStatus(SAVESUCCESS);
                    } else {
                        setSaveStatus(SAVEERROR);
                    }
                    setSaveLoading(false);

                    // Set showSaveStatus back to false after 10 seconds
                    if (saveStatusTimeoutRef.current) {
                        clearTimeout(saveStatusTimeoutRef.current);
                    }
                    saveStatusTimeoutRef.current = setTimeout(() => {
                        setShowSaveStatus(false);
                    }, 10000);
                });
        }

        // Reset add/remove updates
        setGroupMemberUpdates(prev => ({
            ...prev,
            addUpdates: [],
            removeUpdates: []
        }));
    }, [isMocked, groupMemberUpdates, onCloseModal]);

    const onOpenModal = useCallback((type: string) => {
        setOpenModal(true);
        setModalType(type);
    }, []);

    const handleAddUserChange = useCallback(async (value: string) => {
        // Reset userAlreadyAdded to false
        setUserAlreadyAdded(false);

        if (value) {
            await EmployeeService.getNameByID(value)
                .then((response) => {
                    setAddedEmployee({
                        employeeID: value,
                        employeeName: response.data
                    });
                });

            // Check if person is already in the group
            const alreadyInGroup = groupMemberUpdates.updatedGroup.some(
                member => member.employeeID === value
            );
            if (alreadyInGroup) {
                setUserAlreadyAdded(true);
            }
        } else {
            setAddedEmployee(initialAddedEmployee);
        }
    }, [groupMemberUpdates.updatedGroup]);

    const handleProjectChange = useCallback((project: string | undefined) => {
        // handle if project is undefined
        if (!project) return;

        setSelectedProject(project);
        setGroupMemberUpdates({
            addUpdates: [],
            removeUpdates: [],
            updatedGroup: [],
            companyID: project,
            groupName: 1,
            modifiedUser: currentUser.email
        });
        setUserAlreadyAdded(false);
        setChangesMade(false);
        setGroupLoading(true);

        getGroupMembers(project, roleGroup);
    }, [currentUser.email, roleGroup, getGroupMembers]);

    const CurrentGroupMembers = groupLoading ?
        <tr>
            <td colSpan={100}>
                <Delayed waitBeforeShow={750}>
                    <div className="DivPadded DivCenter"><Spinner animation="border" role="status" variant="primary" /></div>
                </Delayed>
            </td>
        </tr> :
            groupMemberUpdates.updatedGroup.length > 0 ?
                groupMemberUpdates.updatedGroup.map((groupMember) =>
                    <tr key={`${groupMember.employeeID}-row`}>
                        <td key={groupMember.employeeID}>{groupMember.employeeName} ({groupMember.employeeID})</td>
                        {canEdit ?
                        <td><ButtonType eventHandler={() => removeMemberLocal(groupMember)} dataName="removeMember" buttonText="x" color="red" size="sm" /></td>
                        : <td></td>}
                    </tr>
                ) :
                <tr>
                    <td colSpan={100}>
                        <b>No Group Members Found</b>
                    </td>
            </tr>;

    return(
        <div className='w-75'>
            <LoadingWrapper showLoading={projectLoading} loadingMessage="Getting DOT Coordinators..." windSpinner windSpinnerScale={3}>
                <Modal show={openModal} onHide={onCloseModal} centered>
                    {modalType === "Add" ? 
                    <div>
                        <Modal.Header closeButton>
                            <Modal.Title>Add DOT Coordinator:</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <div>
                                <ADSearchField onchange={handleAddUserChange} dataName="employeeSearch" title="Select an Employee" />
                                <br></br>
                                {userAlreadyAdded ? 
                                    <div>
                                        <b>This person is already in the group.</b>
                                        <br />
                                    </div>
                                : null}
                                <br />
                                <div className="DivCenter">
                                    <ButtonType
                                        eventHandler={() => addMemberLocal(addedEmployee)}
                                        dataName="addMember"
                                        buttonText="Add"
                                        color="blue"
                                        readOnly={addedEmployee.employeeID === "" || userAlreadyAdded}
                                    />
                                </div>
                            </div>
                        </Modal.Body>
                    </div>
                    : null}
                    {modalType === "Save" ? 
                    <div>
                        <Modal.Header closeButton>
                            <Modal.Title>Are you sure you want to save?</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <div>
                                {groupMemberUpdates.addUpdates.length > 0 ?
                                    <div>
                                        <p><b>Add the following individual(s):</b></p>
                                        <div>
                                            <ul>
                                                {groupMemberUpdates.addUpdates.map((member) =>
                                                <li key={member.employeeID}>{member.employeeName} ({member.employeeID})</li>
                                                )}
                                            </ul>
                                        </div>
                                        <br></br>
                                    </div>
                                : null}
                                {groupMemberUpdates.removeUpdates.length > 0 ?
                                    <div>
                                        <p><b>Remove the following individual(s):</b></p>
                                        <div>
                                            <ul>
                                                {groupMemberUpdates.removeUpdates.map((member) =>
                                                <li key={member.employeeID}>{member.employeeName} ({member.employeeID})</li>
                                                )}
                                            </ul>
                                        </div>
                                        <br></br>
                                    </div>
                                : null}
                                <div className="DivCenter">
                                    <ButtonType eventHandler={saveUpdates} dataName="confirmSave" buttonText="Confirm" color="blue" />
                                </div>
                            </div>
                        </Modal.Body>
                    </div>
                    : null}
                </Modal>

                <ProjectDropdown
                    projects={projectList.projects}
                    onProjectChange={handleProjectChange}
                    cookieName={cookieName ?? ''}
                />
                <h1 className="header mt-5 mb-4 text-center">DOT Coordinator(s)</h1>

                {selectedProject ?
                    <div>
                        <div id="RightAlignedFloatDiv">
                            {canEdit ?
                                changesMade ?
                                    <ButtonType dataName="save" color="blue" buttonText="Save" type="submit" eventHandler={() => onOpenModal("Save")} />
                                : saveLoading ? 
                                    <div className="DivPadded DivCenter"><Spinner animation="border" role="status" variant="primary" /></div>
                                : saveStatus === SAVESUCCESS ?
                                    <div className="DivPadded DivCenter">
                                        <Fade in={showSaveStatus} >
                                            <b>Save Successful ✔</b>
                                        </Fade>
                                    </div>
                                : saveStatus === SAVEERROR ?
                                    <div className="DivPadded DivCenter">
                                        <b>An Error Occurred ⚠️</b><p><small><i>Please Refresh and Try Again</i></small></p>
                                    </div>
                                : null
                            : null}
                        </div>
                        {canEdit ?
                        <div>
                            <div style={{ visibility: (!groupLoading && groupMemberUpdates.updatedGroup.length < 5) ? 'visible' : 'hidden' }}>
                                <AddButton eventHandler={() => onOpenModal("Add")} dataName="DOT" title="DOT Coordinator" readOnly={groupMemberUpdates.updatedGroup.length > 4} />
                            </div>
                            <br />
                            {/* Hint Text */}
                            <p><i>A maximum of 5 individuals can be added.</i></p>
                            <p><i>The individuals identified also need to be added to the project in Blattner Mobile to be added to the DOT Coordinator group and receive Vehicle Inventory Checklist and DVIR tasks. </i></p>
                            <p><i>Upon saving, the changes are emailed to fleetsafety@blattnercompany.com.</i></p>
                        </div>
                        : null}
                        <Table>
                            <thead>
                                <tr>
                                    <th style={{width: 575}}>Group Members</th>
                                    <th style={{width: 25}}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {CurrentGroupMembers}
                            </tbody>
                        </Table>
                    </div>
                : null}
            </LoadingWrapper>
        </div>
    );
}